// FILE: src/app/_components/metrika-consent.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT:
// PURPOSE: Отложенная инициализация Яндекс.Метрики — вызывает ym(id, "init") только после
//          получения согласия пользователя на cookie. Проверяет localStorage oxpa_cookie_consent
//          при монтировании и слушает кастомное событие oxpa:cookie-consent от CookieBanner.
// SCOPE: Замена inline ym("init") в layout.tsx. Работает в паре с CookieBanner.
// INPUT: none (автоматический, константы счётчика)
// OUTPUT: none (сайд-эффект: инициализация Метрики)
// KEYWORDS: DOMAIN(Compliance): CookieConsent; TECH(YandexMetrika): Init; TECH(Privacy): GDPR
// END_MODULE_CONTRACT
//
// START_CHANGE_SUMMARY:
// LAST_CHANGE: 1.1.0 — Идемпотентность: guard-флаг YM_INITED не даёт вызвать ym(id, 'init') дважды.
//  Оба useEffect (проверка сохранённого consent + слушатель оxpа:cookie-consent) могли привести к
//  повторному init (двойной счётчик на главной: pv/vis на / до 1.71 за 90д). Init теперь одноразовый.
// END_CHANGE_SUMMARY

'use client';

import { useEffect } from 'react';

// START_CONSTANTS
const YM_ID = 107253138;
const YM_INIT_OPTS: Record<string, boolean> = {
  clickmap: true,
  trackLinks: true,
  accurateTrackBounce: true,
  webvisor: true,
};
let YM_INITED = false; // guard — однократная инициализация (MEMENTO: не удалять; предотвращает двойной ym init)
// END_CONSTANTS

// START_MODULE_MAP:
// FUNC [8] Инициализация Метрики при согласии => MetrikaConsent
// END_MODULE_MAP

// START_BLOCK_INIT_METRIKA: Функция инициализации с проверкой загрузки ym
function initMetrika(): void {
  // guard: если уже инициализирован в этой сессии — не вызываем ym('init') повторно.
  // MEMENTO: иначе повторный вызов создаёт второй счётчик-сессию и завышает pageviews.
  if (YM_INITED) return;
  if (typeof window.ym === 'undefined') {
    // ym ещё не загрузился — попробуем через 500мс
    setTimeout(initMetrika, 500);
    return;
  }
  YM_INITED = true;
  // ponytail: (window.ym as any) — штатный вызов ym(id, 'init', opts) с Record-опциями.
  // Тип ym в yandex-metrika-goals.tsx не включает третий аргумент-объект, поэтому cast.
  (window.ym as any)(YM_ID, 'init', YM_INIT_OPTS);
}
// END_BLOCK_INIT_METRIKA

export function MetrikaConsent() {
  // START_BLOCK_CHECK_EXISTING_CONSENT: Проверка согласия при монтировании
  useEffect(() => {
    const consent = localStorage.getItem('oxpa_cookie_consent');
    if (consent) {
      initMetrika();
    }
  }, []);
  // END_BLOCK_CHECK_EXISTING_CONSENT

  // START_BLOCK_LISTEN_CUSTOM_EVENT: Слушаем событие от CookieBanner
  useEffect(() => {
    const handleConsent = () => initMetrika();
    window.addEventListener('oxpa:cookie-consent', handleConsent);
    return () => window.removeEventListener('oxpa:cookie-consent', handleConsent);
  }, []);
  // END_BLOCK_LISTEN_CUSTOM_EVENT

  return null;
}
